import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DownloadIcon from '@mui/icons-material/Download';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  Box,
  IconButton,
  Link,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import { useState } from 'react';
import { useRedirect } from 'react-admin';
import { useLocation } from 'react-router-dom';
import { DocumentListItem, DocumentReviewState } from '../../interfaces/document';
import { getLeadDetailsUrl } from '../../util/url';
import { formatPatientName } from '../../utils/patient.utils';
import { buildDocumentUrl } from '../../utils/referralUpload.utils';
import { Column, CustomTable } from '../common/CustomTable';
import { Row } from '../common/CustomTable/types';
import '../common/tab-section.css';
import {
  DOCUMENT_TABLE_CONFIG,
  DOCUMENT_TYPE_COLORS,
  FONT_WEIGHTS,
  TEXT_CONSTANTS,
} from './constants';
import './documents-list.css';
import {
  DOCUMENT_TYPES,
  formatStatusLabel,
  getDocumentStatusColor,
  getReviewStatePresentation,
  isActiveDocumentType,
} from './shared';
import StatusChip from './StatusChip';
import { buildDocumentDetailUrl, DocumentTabName } from './utils/navigationHelpers';

/** The detail tab, by its `?tab=` name (`DOCUMENT_TAB_NAMES`), a document type opens on. */
const DIRECT_TAB_BY_DOCUMENT_TYPE: Record<string, DocumentTabName | undefined> = {
  [DOCUMENT_TYPES.REFERRAL_ORDER]: 'referrals',
  [DOCUMENT_TYPES.PRIOR_AUTHORIZATION]: 'prior-authorizations',
};

interface DocumentsListProps {
  documents: DocumentListItem[];
  loading: boolean;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onRetryProcessing?: (documentId: number) => void;
  onMarkResolved?: (documentId: number) => void;
  onDiscardDocument?: (documentId: number) => void;
}

const DocumentsList: React.FC<DocumentsListProps> = ({
  documents,
  loading,
  pagination,
  onPageChange,
  onRowsPerPageChange,
  onRetryProcessing,
  onMarkResolved,
  onDiscardDocument,
}) => {
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [menuRowId, setMenuRowId] = useState<string | null>(null);
  const isMenuOpen = (rowId: string) => menuRowId === rowId;

  const redirect = useRedirect();
  const location = useLocation();

  // Get current search params to preserve filters when navigating
  const getCurrentSearchParams = () => {
    return location.search;
  };

  const formatDate = (dateString: string): string => {
    return format(new Date(dateString), 'MMM dd, yyyy HH:mm');
  };
  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>, rowId: string) => {
    setMenuAnchorEl(event.currentTarget);
    setMenuRowId(rowId);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
    setMenuRowId(null);
  };

  const getDocumentStatus = (doc: DocumentListItem) => {
    if (doc.requiresReview && isActiveDocumentType(doc.documentType)) {
      return {
        label: 'needs review',
        // Same source as the Review column, which renders the same words in the same list.
        // These were two different reds, from two maps that both claimed to prevent exactly
        // that.
        color: getReviewStatePresentation(DocumentReviewState.NeedsReview).color,
        fontWeight: FONT_WEIGHTS.SEMIBOLD,
      };
    }

    if (!isActiveDocumentType(doc.documentType)) {
      return {
        label: 'classification only',
        color: getDocumentStatusColor('not-enabled'),
        fontWeight: FONT_WEIGHTS.MEDIUM,
      };
    }

    if (doc.crmId && doc.documentType === DOCUMENT_TYPES.REFERRAL_ORDER) {
      return {
        label: 'synced',
        color: getDocumentStatusColor('synced'),
        fontWeight: FONT_WEIGHTS.SEMIBOLD,
      };
    }

    const documentStatus = getDocumentTypeStatus(doc);
    if (documentStatus) {
      return documentStatus;
    }

    if (doc.crmId) {
      return {
        label: 'synced',
        color: getDocumentStatusColor('synced'),
        fontWeight: FONT_WEIGHTS.MEDIUM,
      };
    }

    if (
      doc.isProcessed === true &&
      isActiveDocumentType(doc.documentType) &&
      !doc.referralId &&
      !doc.priorAuthId
    ) {
      return {
        label: 'resolved',
        color: getDocumentStatusColor('completed'),
        fontWeight: FONT_WEIGHTS.MEDIUM,
      };
    }

    return {
      label: '-',
      color: 'text.secondary',
      fontWeight: FONT_WEIGHTS.LIGHT,
    };
  };

  const getDocumentTypeStatus = (doc: DocumentListItem) => {
    switch (doc.documentType) {
      case DOCUMENT_TYPES.REFERRAL_ORDER:
        if (doc.referralStatus) {
          return {
            label: formatStatusLabel(doc.referralStatus),
            color: getDocumentStatusColor(doc.referralStatus),
            fontWeight: FONT_WEIGHTS.SEMIBOLD,
          };
        }
        break;

      case DOCUMENT_TYPES.PRIOR_AUTHORIZATION:
        if (doc.priorAuthStatus) {
          return {
            label: formatStatusLabel(doc.priorAuthStatus),
            color: getDocumentStatusColor(doc.priorAuthStatus),
            fontWeight: FONT_WEIGHTS.SEMIBOLD,
          };
        }
        break;

      default:
        return null;
    }

    return null;
  };

  const renderStatusChip = (doc: DocumentListItem) => {
    const status = getDocumentStatus(doc);

    return (
      <StatusChip
        label={status.label}
        color={status.color}
        fontWeight={status.fontWeight}
        showAsText={status.label === '-'}
      />
    );
  };

  const hasCompletedButNoData = (doc: DocumentListItem): boolean => {
    return (
      doc.processingStatus === 'completed' &&
      (doc.processingStep === 'extraction' || doc.processingStep === 'post-processing') &&
      doc.isProcessed === false &&
      (!doc.patientDocumentId || doc.patientName === 'No Patient Data') &&
      !doc.extractionFailed &&
      isActiveDocumentType(doc.documentType)
    );
  };

  const handleViewDocument = (document: DocumentListItem, directToTab = false) => {
    // By name, as the Referrals list opens it: a business tab's index depends on which
    // document types were detected and in what order, so a fixed `2` opened the wrong tab on a
    // package carrying both a referral and a prior authorization.
    const tab =
      directToTab && isActiveDocumentType(document.documentType)
        ? DIRECT_TAB_BY_DOCUMENT_TYPE[document.documentType]
        : undefined;

    // Preserve current search params (filters)
    redirect(buildDocumentDetailUrl(document.documentId, tab, getCurrentSearchParams()));
  };

  const handleDownloadDocument = (document: DocumentListItem) => {
    const s3Url = buildDocumentUrl(document.s3Key);
    if (s3Url) {
      window.open(s3Url, '_blank');
    }
  };

  const handleRetryProcessing = (document: DocumentListItem) => {
    if (onRetryProcessing) {
      onRetryProcessing(document.documentId);
    }
    handleCloseMenu();
  };

  const handleMarkResolved = (document: DocumentListItem) => {
    if (onMarkResolved) {
      onMarkResolved(document.documentId);
    }
    handleCloseMenu();
  };

  const handleDiscardDocument = (document: DocumentListItem) => {
    if (onDiscardDocument) {
      onDiscardDocument(document.documentId);
    }
    handleCloseMenu();
  };

  const columns: Column[] = [
    {
      key: 'document',
      label: 'Document',
      minWidth: DOCUMENT_TABLE_CONFIG.COLUMN_MIN_WIDTHS.DOCUMENT,
    },
    { key: 'clinic', label: 'Clinic', minWidth: DOCUMENT_TABLE_CONFIG.COLUMN_MIN_WIDTHS.CLINIC },
    { key: 'type', label: 'Type', minWidth: DOCUMENT_TABLE_CONFIG.COLUMN_MIN_WIDTHS.TYPE },
    {
      key: 'processing',
      label: 'Review',
      minWidth: DOCUMENT_TABLE_CONFIG.COLUMN_MIN_WIDTHS.PROCESSING,
    },
    {
      key: 'status',
      label: 'Workflow Status',
      minWidth: DOCUMENT_TABLE_CONFIG.COLUMN_MIN_WIDTHS.STATUS,
    },
    { key: 'patient', label: 'Patient', minWidth: DOCUMENT_TABLE_CONFIG.COLUMN_MIN_WIDTHS.PATIENT },
    { key: 'created', label: 'Created', minWidth: DOCUMENT_TABLE_CONFIG.COLUMN_MIN_WIDTHS.CREATED },
    {
      key: 'actions',
      label: 'Actions',
      minWidth: DOCUMENT_TABLE_CONFIG.COLUMN_MIN_WIDTHS.ACTIONS,
      align: 'center',
    },
  ];

  const rows: Row[] = documents.map((doc) => {
    const uniqueRowId = `${doc.documentId}-${doc.detectedDocumentId || 'no-detected'}-${
      doc.patientDocumentId || 'no-patient'
    }`;

    return {
      document: (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant='body2' fontWeight={FONT_WEIGHTS.MEDIUM} color='text.primary'>
              {doc.externalId || doc.documentId}
            </Typography>
            {doc.isDuplicate && (
              <StatusChip
                label='DUPLICATE'
                color='#f59e0b'
                backgroundColor='#fffbeb'
                borderColor='#f59e0b'
                fontWeight='semibold'
                variant='outlined'
                size='small'
              />
            )}
          </Box>
          <Typography variant='caption' color='text.secondary'>
            {doc.source.replace('-', ' ')}
          </Typography>
        </Box>
      ),
      clinic: (
        <Box>
          {doc.receivingClinicId ? (
            <>
              <Typography variant='body2' fontWeight={FONT_WEIGHTS.MEDIUM} color='text.primary'>
                Selected: {doc.receivingClinicId}
              </Typography>
              {doc.detectedClinicId && doc.detectedClinicId !== doc.receivingClinicId && (
                <Typography variant='caption' color='#0891b2' display='block'>
                  Detected: {doc.detectedClinicId}
                </Typography>
              )}
            </>
          ) : doc.detectedClinicId ? (
            <Typography variant='body2' color='#0891b2' fontWeight={FONT_WEIGHTS.MEDIUM}>
              Detected: {doc.detectedClinicId}
            </Typography>
          ) : (
            <Typography variant='body2' color='#6b7280' fontWeight={FONT_WEIGHTS.MEDIUM}>
              {TEXT_CONSTANTS.NO_CLINIC_ASSIGNED}
            </Typography>
          )}
        </Box>
      ),
      type: (
        <Box>
          <StatusChip
            label={doc.documentType?.replace('-', ' ') || TEXT_CONSTANTS.UNKNOWN}
            color={
              doc.documentType === DOCUMENT_TYPES.REFERRAL_ORDER
                ? DOCUMENT_TYPE_COLORS.REFERRAL_ORDER.COLOR
                : doc.documentType === DOCUMENT_TYPES.PRIOR_AUTHORIZATION
                ? DOCUMENT_TYPE_COLORS.PRIOR_AUTHORIZATION.COLOR
                : DOCUMENT_TYPE_COLORS.DEFAULT.COLOR
            }
            backgroundColor={
              doc.documentType === DOCUMENT_TYPES.REFERRAL_ORDER
                ? DOCUMENT_TYPE_COLORS.REFERRAL_ORDER.BACKGROUND
                : doc.documentType === DOCUMENT_TYPES.PRIOR_AUTHORIZATION
                ? DOCUMENT_TYPE_COLORS.PRIOR_AUTHORIZATION.BACKGROUND
                : DOCUMENT_TYPE_COLORS.DEFAULT.BACKGROUND
            }
            borderColor={
              doc.documentType === DOCUMENT_TYPES.REFERRAL_ORDER
                ? DOCUMENT_TYPE_COLORS.REFERRAL_ORDER.BORDER
                : doc.documentType === DOCUMENT_TYPES.PRIOR_AUTHORIZATION
                ? DOCUMENT_TYPE_COLORS.PRIOR_AUTHORIZATION.BORDER
                : DOCUMENT_TYPE_COLORS.DEFAULT.BORDER
            }
            fontWeight={
              isActiveDocumentType(doc.documentType) ? FONT_WEIGHTS.SEMIBOLD : FONT_WEIGHTS.MEDIUM
            }
            variant='outlined'
          />
          <Typography variant='caption' color='text.secondary' display='block' sx={{ mt: 0.5 }}>
            {Math.round(doc.confidence * 100)}
            {TEXT_CONSTANTS.CONFIDENCE_SUFFIX}
          </Typography>
        </Box>
      ),
      processing: (() => {
        // The server-derived state, not the raw pipeline status. This column used to render
        // `processingStatus` verbatim - one of 19 values, meaningful only alongside the step -
        // so an operator had to know the pipeline to tell "waiting" from "broken". The raw
        // position stays one hover away, and in full on the Processing Details tab.
        const review = getReviewStatePresentation(doc.reviewState);

        return (
          <Box>
            <Tooltip
              title={`${review.description} (pipeline: ${doc.processingStep} / ${doc.processingStatus})`}
              arrow>
              {/*
                `tabIndex` so the pipeline detail is reachable without a mouse. The span is
                here because a Tooltip needs a DOM node to anchor to, and a bare one is not
                focusable — which put the only explanation of *why* a document needs review
                out of reach of keyboard and screen-reader users.
              */}
              <span tabIndex={0}>
                <StatusChip
                  label={review.label}
                  color={review.color}
                  fontWeight={FONT_WEIGHTS.SEMIBOLD}
                />
              </span>
            </Tooltip>
            {doc.processingRetryCount > 0 && (
              <Typography variant='caption' color='text.secondary' display='block' sx={{ mt: 0.5 }}>
                {doc.processingRetryCount} retr{doc.processingRetryCount === 1 ? 'y' : 'ies'}
              </Typography>
            )}
            {doc.extractionFailed && (
              <Typography
                variant='caption'
                color='#dc2626'
                display='block'
                sx={{ mt: 0.5, fontWeight: FONT_WEIGHTS.SEMIBOLD }}>
                Extraction failed
              </Typography>
            )}
          </Box>
        );
      })(),
      status: <Box>{renderStatusChip(doc)}</Box>,
      patient: (
        <Box>
          {!isActiveDocumentType(doc.documentType) ? (
            <Typography variant='body2' color='text.secondary'>
              -
            </Typography>
          ) : (
            <>
              <Typography variant='body2' fontWeight={FONT_WEIGHTS.MEDIUM} color='text.primary'>
                {formatPatientName(doc.patientName)}
              </Typography>
              {doc.patientIdentifier && (
                <Typography variant='caption' color='text.secondary' display='block'>
                  {doc.patientIdentifier}
                </Typography>
              )}
              {doc?.crmId && (
                <Link
                  component='button'
                  variant='caption'
                  onClick={() => window.open(getLeadDetailsUrl(doc.crmId!), '_blank')}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    mt: 0.5,
                    color: '#0891b2',
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' },
                  }}>
                  View in CRM
                  <OpenInNewIcon sx={{ fontSize: 12 }} />
                </Link>
              )}
            </>
          )}
        </Box>
      ),
      created: (
        <Box>
          <Typography variant='body2' fontWeight={FONT_WEIGHTS.LIGHT}>
            {formatDate(doc.documentCreatedAt)}
          </Typography>
        </Box>
      ),
      actions: (
        <Box>
          <Box>
            <IconButton onClick={(e) => handleOpenMenu(e, uniqueRowId)} size='small'>
              <MoreVertIcon />
            </IconButton>
            <Menu open={isMenuOpen(uniqueRowId)} anchorEl={menuAnchorEl} onClose={handleCloseMenu}>
              {/* View Original Document for duplicates */}
              {doc.isDuplicate && doc.originalDocumentId && (
                <MenuItem
                  onClick={() => {
                    window.open(`#/documents/library/${doc.originalDocumentId}`, '_blank');
                    handleCloseMenu();
                  }}>
                  <ListItemIcon>
                    <OpenInNewIcon fontSize='small' />
                  </ListItemIcon>
                  <ListItemText>View Original Referral</ListItemText>
                </MenuItem>
              )}

              {isActiveDocumentType(doc.documentType) &&
              !doc.isDuplicate &&
              ((doc.documentType === DOCUMENT_TYPES.REFERRAL_ORDER && doc.referralId) ||
                (doc.documentType === DOCUMENT_TYPES.PRIOR_AUTHORIZATION && doc.priorAuthId)) ? (
                <MenuItem
                  onClick={() => {
                    handleViewDocument(doc, true);
                    handleCloseMenu();
                  }}>
                  <ListItemIcon>
                    <VisibilityIcon fontSize='small' />
                  </ListItemIcon>
                  <ListItemText>
                    View{' '}
                    {doc.documentType === DOCUMENT_TYPES.REFERRAL_ORDER
                      ? 'Referral'
                      : doc.documentType === DOCUMENT_TYPES.PRIOR_AUTHORIZATION
                      ? 'Prior Authorization'
                      : 'Details'}
                  </ListItemText>
                </MenuItem>
              ) : null}

              {/* General view details */}
              <MenuItem
                onClick={() => {
                  handleViewDocument(doc, false);
                  handleCloseMenu();
                }}>
                <ListItemIcon>
                  <VisibilityIcon fontSize='small' />
                </ListItemIcon>
                <ListItemText>View Details</ListItemText>
              </MenuItem>

              {/* Actions for documents that need reprocessing or review */}
              {hasCompletedButNoData(doc) && (
                <MenuItem onClick={() => handleRetryProcessing(doc)}>
                  <ListItemIcon>
                    <RefreshIcon fontSize='small' sx={{ color: '#0891b2' }} />
                  </ListItemIcon>
                  <ListItemText>Re-process Document</ListItemText>
                </MenuItem>
              )}

              {/* Mark as resolved action for any document that needs review or reprocessing */}
              {(hasCompletedButNoData(doc) || doc.requiresReview) && (
                <MenuItem onClick={() => handleMarkResolved(doc)}>
                  <ListItemIcon>
                    <CheckCircleIcon fontSize='small' sx={{ color: '#10b981' }} />
                  </ListItemIcon>
                  <ListItemText>Mark as Resolved</ListItemText>
                </MenuItem>
              )}

              {/* Actions for failed extraction */}
              {doc.extractionFailed && (
                <>
                  <MenuItem onClick={() => handleRetryProcessing(doc)}>
                    <ListItemIcon>
                      <RefreshIcon fontSize='small' sx={{ color: '#0891b2' }} />
                    </ListItemIcon>
                    <ListItemText>Retry Extraction</ListItemText>
                  </MenuItem>
                  <MenuItem onClick={() => handleDiscardDocument(doc)}>
                    <ListItemIcon>
                      <RemoveCircleOutlineIcon fontSize='small' sx={{ color: '#ef4444' }} />
                    </ListItemIcon>
                    <ListItemText>Discard Document</ListItemText>
                  </MenuItem>
                </>
              )}

              {/* Download action — hidden when the document has no PDF in S3 */}
              {doc.s3Key && (
                <MenuItem onClick={() => handleDownloadDocument(doc)}>
                  <ListItemIcon>
                    <DownloadIcon fontSize='small' />
                  </ListItemIcon>
                  <ListItemText>Download PDF</ListItemText>
                </MenuItem>
              )}
            </Menu>
          </Box>
        </Box>
      ),
    };
  });

  return (
    <Box className='documents-list-container'>
      <CustomTable
        columns={columns}
        rows={rows}
        loading={loading}
        tableSize='small'
        defaultText={TEXT_CONSTANTS.DEFAULT_TABLE_TEXT}
        maxHeight={DOCUMENT_TABLE_CONFIG.MAX_HEIGHT}
        stickyHeader={true}
        pagination={{
          rowsPerPageOptions: [...DOCUMENT_TABLE_CONFIG.PAGINATION.ROWS_PER_PAGE_OPTIONS],
          rowPerPage: pagination.limit,
          page: pagination.page,
          totalCount: pagination.total,
          onPageChange,
          onRowsPerPageChange,
        }}
      />
    </Box>
  );
};

export default DocumentsList;
